/**
 * useRealtimeMessages Hook
 *
 * Subscribes to Supabase Realtime for chat_messages table
 * to replace 30-second polling with instant updates.
 * Updates unread message counts and message lists in real-time.
 *
 * Uses the base useRealtimeSubscription hook for:
 * - Automatic reconnection with exponential backoff
 * - Connection status tracking
 * - Error handling with Sentry integration
 * - Debounced query invalidation
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtimeSubscription, RealtimeStatus } from './useRealtimeSubscription';

interface UseRealtimeMessagesOptions {
  userId?: string;
  enabled?: boolean;
  onNewMessage?: (message: unknown) => void;
  onStatusChange?: (status: RealtimeStatus) => void;
}

interface UseRealtimeMessagesResult {
  /** Current connection status */
  status: RealtimeStatus;
  /** Error if status is 'error' */
  error: Error | null;
  /** Manually unsubscribe */
  unsubscribe: () => void;
  /** Manually retry connection */
  retry: () => void;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
}

export function useRealtimeMessages(
  options: UseRealtimeMessagesOptions = {}
): UseRealtimeMessagesResult {
  const { userId, enabled = true, onNewMessage, onStatusChange } = options;
  const queryClient = useQueryClient();

  // Memoize the change handler to prevent re-subscriptions
  const handlePostgresChange = useCallback(
    (_table: string, payload: { eventType: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
      const data = (payload.new || payload.old) as { sender_id?: string; recipient_id?: string } | undefined;

      // Only process if this message involves the current user
      // (Supabase Realtime doesn't support OR filters, so we filter client-side)
      if (!data || (data.sender_id !== userId && data.recipient_id !== userId)) {
        return;
      }

      // Invalidate unread messages count
      queryClient.invalidateQueries({ queryKey: ['unreadMessages'] });

      // Invalidate messages list (for conversation view) - with userId for exact match
      queryClient.invalidateQueries({ queryKey: ['messages', userId] });
      queryClient.invalidateQueries({ queryKey: ['messages'] }); // Keep for backwards compatibility
      queryClient.invalidateQueries({ queryKey: ['/api/chat_messages'] });

      // Invalidate conversations list
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      // Also invalidate the specific API endpoint that MessagesPage uses
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/messages/conversations'] });

      // CRITICAL: Invalidate the active chat query so MessagesPage shows new messages in real-time
      // This invalidates ALL chat queries, which will trigger a refetch for the active conversation
      queryClient.invalidateQueries({ queryKey: ['chat'] });

      // Call optional callback for new messages (only for INSERT)
      if (payload.eventType === 'INSERT' && onNewMessage) {
        onNewMessage(payload.new);
      }
    },
    [userId, queryClient, onNewMessage]
  );

  // Memoize tables configuration
  // Note: We can't use OR filter in Supabase Realtime, so we subscribe to all
  // changes and filter client-side based on sender_id/recipient_id
  const tables = useMemo(
    () => [
      {
        table: 'chat_messages',
        event: '*' as const,
        // No filter - we filter client-side for OR condition (sender_id OR recipient_id)
      },
    ],
    []
  );

  // Use the base subscription hook
  const subscription = useRealtimeSubscription({
    channelName: `messages-${userId}`,
    enabled: enabled && !!userId,
    tables,
    onPostgresChange: handlePostgresChange,
    onStatusChange,
    debounceMs: 300, // Debounce rapid updates
  });

  return {
    status: subscription.status,
    error: subscription.error,
    unsubscribe: subscription.unsubscribe,
    retry: subscription.retry,
    reconnectAttempts: subscription.reconnectAttempts,
  };
}

export default useRealtimeMessages;
